import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, retry, timer, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AuthUser,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse
} from '../models/auth.models';
import { ParentRegisterPayload } from '../models/parent-register.model';
export const ROLE_ID = {
  Admin:   1,
  Teacher: 2,
  Student: 3,
  Parent:  4
} as const;

export type RoleName = 'admin' | 'teacher' | 'student' | 'parent';

@Injectable({ providedIn: 'root' })
export class AuthService {

  private readonly baseUrl = environment.gatewayBaseUrl.replace(/\/$/, '');

  private _isLoggedIn = new BehaviorSubject<boolean>(this.hasValidToken());
  private _user       = new BehaviorSubject<AuthUser | null>(this.getUserFromStorage());

  isLoggedIn$ = this._isLoggedIn.asObservable();
  user$       = this._user.asObservable();

  constructor(private http: HttpClient) {
    // Sanity check: warn loudly in dev if the base URL looks like it's
    // missing an /api segment, since that mismatch produces confusing
    // 404s that look like generic connectivity failures in the UI.
    if (!this.baseUrl.includes('/api')) {
      console.warn(
        `[AuthService] gatewayBaseUrl ("${this.baseUrl}") does not include "/api". ` +
        `Confirm this matches your backend controller's route prefix exactly.`
      );
    }
  }

  private hasValidToken(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const payload = JSON.parse(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );

      if (!payload.exp) return true;

      const isExpired = Date.now() >= payload.exp * 1000;
      if (isExpired) {
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('auth_user');
        return false;
      }

      return true;
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('auth_user');
      return false;
    }
  }

  private getUserFromStorage(): AuthUser | null {
    const stored = localStorage.getItem('auth_user');
    if (!stored) return null;
    try { return JSON.parse(stored) as AuthUser; }
    catch { return null; }
  }

  private mapRoleIdToName(userTypeId: number | undefined | null): RoleName | null {
    switch (userTypeId) {
      case ROLE_ID.Admin:   return 'admin';
      case ROLE_ID.Teacher: return 'teacher';
      case ROLE_ID.Student: return 'student';
      case ROLE_ID.Parent:  return 'parent';
      default:
        console.warn(`[AuthService] Unrecognized userTypeId: ${userTypeId}`);
        return null;
    }
  }

  // ── [POST] /auth/p/login ──────────────────────────────────────────
  login(email: string, password: string): Observable<LoginResponse> {
    const url  = `${this.baseUrl}/auth/p/login`;
    const body: LoginRequest = { email, password };

    return this.http.post<LoginResponse>(url, body).pipe(
      tap((res) => {
        const role = this.mapRoleIdToName(res.user?.userTypeId);
        if (!role) {
          console.error('[AuthService] Could not map userTypeId:', res.user?.userTypeId);
        }
        const user: AuthUser = {
          Email: res.user?.email ?? email,
          role:  role as RoleName,
          token: res.token,
          userId: res.user?.userId,
          userName: res.user?.userName,
          firstName: res.user?.firstName
        };
        this._isLoggedIn.next(true);
        this._user.next(user);

        if (res.token)        localStorage.setItem('token',    res.token);
        if (res.user?.userId) localStorage.setItem('userId',   res.user.userId);
        localStorage.setItem('auth_user', JSON.stringify(user));
      })
    );
  }

  // ── [POST] /auth/p/register ───────────────────────────────────────
  // Retries once, after a short delay, ONLY on transient failures
  // (network-level errors with status 0, or a 5xx from the server) —
  // this covers cold-start / connection-pool blips that cause the first
  // attempt to fail even though the request itself was perfectly valid.
  // Genuine validation errors (400/409/etc.) are NOT retried — they'll
  // fail identically every time, so retrying would be pointless.
  register(payload: RegisterRequest): Observable<RegisterResponse> {
    const url = `${this.baseUrl}/auth/p/register`;

    const formData = new FormData();
    formData.append('FirstName',       payload.firstName);
    formData.append('LastName',        payload.lastName);
    formData.append('Username',        payload.username);
    formData.append('Email',           payload.email);
    formData.append('Phone',           payload.phone);
    formData.append('Password',        payload.password);
    formData.append('ConfirmPassword', payload.confirmPassword);
    formData.append('UserTypeId',      payload.userTypeId.toString());

    // NOTE: IsEmailVerified, AgreemengtAcceptedDate, and PrivacyConsent
    // are intentionally NOT sent from the client. These represent
    // trust/consent decisions that should be established server-side
    // (e.g. after an actual email verification flow, or a real consent
    // capture step) — never dictated by whatever the client happens to
    // include in a form post. The backend already defaults
    // AgreementAcceptedDate to DateTime.UtcNow when omitted.

    if (payload.profilePictureFile) {
      formData.append('ProfileImage', payload.profilePictureFile);
    }

    return this.http.post<RegisterResponse>(url, formData).pipe(
      retry({
        count: 1, // one silent retry only
        delay: (error: HttpErrorResponse) => {
          const isTransient = error.status === 0 || error.status >= 500;
          if (!isTransient) {
            // Real validation/business error (400, 409, etc.) — don't retry,
            // surface it immediately.
            return throwError(() => error);
          }
          console.warn(`[AuthService] Transient error on register (status ${error.status}), retrying once...`);
          return timer(800); // brief delay before the single retry
        }
      })
    );
  }

  // ── Parent Registration ────────────────────────────────────────────────────────
  parentRegister(payload: ParentRegisterPayload): Observable<any> {
    const url = `${this.baseUrl}/Auth/p/parent-registration`;
    return this.http.post(url, payload);
  }

  getInviteDetails(studentId: string, inviteCode: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/StudentPerformance/r/GetInvitationDetails?studentId=${studentId}&inviteCode=${inviteCode}`
    );
  }

  // ── Forgot Password  ────────────────────────────────────────────────────────
  forgotPassword(email: string) {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/Auth/p/forgot-password`,
      { email }
    );
  }

  // ── Reset Password ────────────────────────────────────────────────────────
  resetPassword(data: any) {
    return this.http.post(
      `${this.baseUrl}/Auth/p/reset-password`,
      data
    );
  }

  // ── logout ────────────────────────────────────────────────────────
  logout(): void {
    this._isLoggedIn.next(false);
    this._user.next(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('auth_user');
  }

  // ── helpers ───────────────────────────────────────────────────────
  getCurrentRole(): RoleName | null { return this._user.getValue()?.role ?? null; }
  getCurrentUser(): AuthUser | null  { return this._user.getValue(); }
  isAuthenticated(): boolean         { return this._isLoggedIn.getValue(); }

  getUserId(): string | null {
    const stored = localStorage.getItem('userId');
    if (stored) return stored;

    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const payload = JSON.parse(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );

      const userId =
        payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
        || payload['sub']
        || payload['userId']
        || null;

      if (userId) localStorage.setItem('userId', userId);
      return userId;
    } catch (e) {
      console.error('[AuthService] Failed to decode JWT:', e);
      return null;
    }
  }

  getUserName(): string | null {
    return this._user.getValue()?.userName ?? null;
  }

  getFirstName(): string | null {
    return this._user.getValue()?.firstName ?? null;
  }
}
